import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDaysIcon, CheckCircleIcon, ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Sidebar from '../components/Sidebar';

const leavesData = [
  { id: 1, name: "John Doe", type: "Sick Leave", from: "2025-09-01", to: "2025-09-03", status: "Approved" },
  { id: 2, name: "Jane Smith", type: "Casual Leave", from: "2025-09-10", to: "2025-09-11", status: "Pending" },
  { id: 3, name: "Bob Johnson", type: "Paid Leave", from: "2025-09-15", to: "2025-09-18", status: "Rejected" },
  { id: 4, name: "Alice Williams", type: "Personal Leave", from: "2025-09-20", to: "2025-09-21", status: "Approved" },
];

const Leaves = () => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredLeaves = leavesData.filter(leave =>
        leave.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.status.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved':
                return 'bg-green-100 text-green-800';
            case 'Pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'Rejected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const totalApproved = leavesData.filter(l => l.status === 'Approved').length;
    const totalPending = leavesData.filter(l => l.status === 'Pending').length;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="p-6 space-y-8"
        >
            <h1 className="text-3xl font-extrabold text-gray-800">Leave Management</h1>

            {/* 🔹 Stat Cards and Actions */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <motion.div 
                    className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="flex items-center space-x-4">
                        <div className="p-3 rounded-full bg-indigo-100">
                            <CalendarDaysIcon className="h-8 w-8 text-indigo-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">Total Leave Requests</p>
                            <h3 className="text-2xl font-bold mt-1 text-indigo-800">{leavesData.length}</h3>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-500">Pending Approvals</p>
                            <h3 className="text-2xl font-bold mt-1 text-yellow-800">{totalPending}</h3>
                        </div>
                        <div className="p-3 rounded-full bg-yellow-100">
                            <ClockIcon className="h-8 w-8 text-yellow-600" />
                        </div>
                    </div>
                </motion.div>
                
                <motion.button 
                    className="flex items-center justify-center p-6 bg-indigo-600 rounded-2xl shadow-lg text-white font-semibold transition-colors duration-200 hover:bg-indigo-700"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                >
                    <CheckCircleIcon className="h-6 w-6 mr-2" />
                    Submit Leave Request
                </motion.button>
            </section>

            {/* 🔹 Search and Leave List */}
            <section className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 space-y-6">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full rounded-full border-0 py-3 pl-10 pr-4 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        placeholder="Search by name, leave type, or status..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Leave Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredLeaves.length > 0 ? (
                        filteredLeaves.map((leave, index) => (
                            <motion.div
                                key={leave.id}
                                className="bg-gray-50 p-6 rounded-xl shadow-md border border-gray-200 space-y-3 transition-transform transform hover:scale-105 duration-300"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                <div className="flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-gray-800">{leave.name}</h3>
                                    <span className={`px-3 py-1 text-xs font-semibold rounded-full uppercase ${getStatusColor(leave.status)}`}>
                                        {leave.status}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500 font-medium">{leave.type} Leave</p>
                                <div className="text-sm text-gray-600">
                                    <p>From: <span className="font-semibold">{leave.from}</span></p>
                                    <p>To: <span className="font-semibold">{leave.to}</span></p>
                                </div>
                            </motion.div>
                        ))
                    ) : (
                        <div className="lg:col-span-3 text-center py-12 text-gray-500">
                            <p>No leave requests found matching your search criteria.</p>
                        </div>
                    )}
                </div>
            </section>
        </motion.div>
    );
};

export default Leaves;